import { makeStyles, styled } from "@material-ui/core";
import { attributes } from "@type-r/models";
import { useCollection, useIO, useModel } from "@type-r/react";
import { Button, Flex, div, getIdFromPath, navigate } from "lqx-react";
import React, { useMemo } from "react";
import { UserEntitlementCatalogs } from "src/app/models/UserEntitlements";
import { Can } from "src/app/services/can";
import { AssetsSummary } from "./api/assetsSummary";
import { ConnectionCount } from "./api/connectionCount";
import { ImportJobCount } from "./api/importJobCount";
import { ImportTemplateCount } from "./api/importTemplateCount";
import { PolicyCardBound } from "./api/policyCardBound";
import { PolicyCardDraft } from "./api/policyCardDraft";
import { DigitizationMenuItem, DigitizationMenuRegistryInstance } from "./menu-registry";
import { ValidationRuleCount } from "./api/validationRuleCount";
import { globalStore } from "api/store";

const DigitizationMenuState = attributes({
    pendingPoliciesCount: Number,
    pendingPoliciesCountView: '0',
    digitizedPoliciesCount: Number,
    digitizedPoliciesCountView: '0',
    importTemplateCount: Number,
    importTemplateCountView: '0',
    validationRuleCount: Number,
    validationRuleCountView: '0',
    invoicesCount: Number,
    invoicesCountView: '0',
    paymentsCount: Number,
    paymentsCountView: '0',
    creditNotesCount: Number,
    creditNotesCountView: '0',
    remittanceAdviceCount: Number,
    remittanceCountView: '0',
    unappliedCashCount: Number,
    unappliedCashCountView: '0',
    importJobCount: Number,
    connectionCount: Number,
    importJobCountView: '0',
    allPoliciesCountView: '0',
    allAssetsCountView: '0',
    connectionsCountView: '0'
});

export const DigitizationNavigationMenu = () => {
    const classes = makeNavigationCategoryStyles();
    const state = useModel(DigitizationMenuState);
    const pendingPolicies = useCollection.of(PolicyCardDraft);
    //TODO: policies will be counted either through an insurance api or directly from asset master
    const digitizedPolicies = useCollection.of(PolicyCardBound);
    const importTemplateCountModel = useModel(ImportTemplateCount);
    const assetsSummary = useModel(AssetsSummary);
    const importJobCountModel = useModel(ImportJobCount);
    const connectionCountModel = useModel(ConnectionCount);
    const validationRuleCountModel = useModel(ValidationRuleCount);
    const getCorrectItemsCount = (count) => `${count > 9999 ? 9999 : count}${count > 9999 ? '+' : ''}`;
    const setItemsViewCount = (count, view) => view.set(getCorrectItemsCount(count));
    const setAllAssetsCount = () => setItemsViewCount(state.pendingPoliciesCount + state.digitizedPoliciesCount + state.invoicesCount + state.paymentsCount + state.creditNotesCount + state.unappliedCashCount, state.$.allAssetsCountView);
    const setAllPoliciesCount = () => setItemsViewCount(state.pendingPoliciesCount + state.digitizedPoliciesCount, state.$.allPoliciesCountView);

    useIO(async () => {
        await pendingPolicies.fetch().catch(e => console.error(e));
        state.$.pendingPoliciesCount.set(pendingPolicies.length);
        setItemsViewCount(pendingPolicies.length, state.$.pendingPoliciesCountView);
        setAllPoliciesCount();
    });

    useIO(async () => {
        await importTemplateCountModel.fetch().catch(e => console.error(e));
        state.importTemplateCount = importTemplateCountModel.count;
        setItemsViewCount(importTemplateCountModel.count, state.$.importTemplateCountView);
    });

    useIO(async () => {
        await importJobCountModel.fetch().catch(e => console.error(e));
        state.importJobCount = importJobCountModel.count;
        setItemsViewCount(importJobCountModel.count, state.$.importJobCountView);
    });

    useIO(async () => {
        await digitizedPolicies.fetch().catch(e => console.error(e));
        state.$.digitizedPoliciesCount.set(digitizedPolicies.length);
        setItemsViewCount(digitizedPolicies.length, state.$.digitizedPoliciesCountView);
        setAllPoliciesCount();
        setAllAssetsCount();
    });

    useIO(async () => {
        await connectionCountModel.fetch().catch(e => console.error(e));
        state.connectionCount = connectionCountModel.count;
        setItemsViewCount(connectionCountModel.count, state.$.connectionsCountView);
    });

    useIO(async () => {
        await validationRuleCountModel.fetch().catch(e => console.error(e));
        state.validationRuleCount = validationRuleCountModel.count;
        setItemsViewCount(validationRuleCountModel.count, state.$.validationRuleCountView);
    });

    useIO(async () => {
        await assetsSummary.fetch().catch(e => handlePollingError(e, assetsSummary));
        await globalStore.assetTypesLookup.fetch();
        state.$.invoicesCount.set(assetsSummary.invoicesCount);
        state.$.paymentsCount.set(assetsSummary.paymentsCount);
        state.$.creditNotesCount.set(assetsSummary.creditNotesCount);
        state.$.remittanceAdviceCount.set(assetsSummary.remittanceAdviceCount);
        state.$.unappliedCashCount.set(assetsSummary.unappiedCashesCount);
        setItemsViewCount(assetsSummary.invoicesCount, state.$.invoicesCountView);
        setItemsViewCount(assetsSummary.paymentsCount, state.$.paymentsCountView);
        setItemsViewCount(assetsSummary.creditNotesCount, state.$.creditNotesCountView);
        setItemsViewCount(assetsSummary.remittanceAdviceCount, state.$.remittanceCountView);
        setItemsViewCount(assetsSummary.unappiedCashesCount, state.$.unappliedCashCountView);
        setAllAssetsCount();
    });


    const visibleMenuCategories = useMemo(() => DigitizationMenuRegistryInstance.getVisibleCategories(), [true]);

    const handlePollingError = (error, assetsSummary) => {
        console.error(error);
        assetsSummary.$.error.set("Failed to get assets summary");
    }

    const getCount = (menuItem: DigitizationMenuItem) => {
        switch (menuItem.path) {
            case 'import-jobs': return state.importJobCountView;
            case 'insurancepolicies': return state.allPoliciesCountView;
            case 'invoices': return state.invoicesCountView;
            case 'payments': return state.paymentsCountView;
            case 'remittanceadvice': return state.remittanceCountView;
            case 'unappliedCashes': return state.unappliedCashCountView;
            case 'creditNotes': return state.creditNotesCountView;
            case 'connections': return state.connectionsCountView;
            case 'import-templates': return state.importTemplateCountView;
            case 'validation-rules': return state.validationRuleCountView;
        }
        return null;
    }

    return (
        <>
            <MenuContainer>
                <NavigationMenu>
                    {Can(UserEntitlementCatalogs.ViewInBlockAnAssetButton) &&
                        <li>
                            <InBlockButton onClick={() => navigate('')}>Digitize an Asset</InBlockButton>
                        </li>
                    }
                    {visibleMenuCategories.map(menuCategory =>
                        <>
                            <NavigationCategory>{menuCategory.label}</NavigationCategory>
                            {DigitizationMenuRegistryInstance.getVisibleChildren(menuCategory).map(menuItem =>
                                <NavigationItem path={menuItem.path}>
                                    <Flex className={classes.menuItemWithLowerFontSize} row>{menuItem.label} {getCount(menuItem) != null && <NavigationItemBullet>{getCount(menuItem)}</NavigationItemBullet>}</Flex>
                                </NavigationItem>
                            )}
                        </>
                    )}
                </NavigationMenu>
            </MenuContainer>
        </>
    )
};

const MenuContainer = div({
    backgroundColor: '#112C4A',
    height: '100%',
    width: '100%',
    minWidth: '200px'
});

const InBlockButton = styled(Button)({
    backgroundColor: '#163C66',
    color: '#58C1EE',
    fontSize: '14px !important',
    fontWeight: 600,
    margin: '20px',
    borderColor: '#58C1EE',
    borderRadius: '4px',
    borderWidth: 1,
    '&:hover': {
        backgroundColor: '#163C66',
        fontSize: '14px !important',
        fontWeight: 600,
        color: 'white !important',
    },
    '&:active': {
        backgroundColor: '#163C66',
        color: '#58C1EE',
        fontSize: '14px !important',
        fontWeight: 600,
    },
    '&:focus': {
        backgroundColor: '#163C66',
        fontSize: '14px !important',
        fontWeight: 600,
    }
});

const NavigationMenu = styled('ul')({
    listStyleType: 'none',
    color: 'white',
    textTransform: 'uppercase',
    paddingLeft: 0,
    backgroundColor: '#112C4A'
});

const NavigationCategory = ({ children }) => {
    const classes = makeNavigationCategoryStyles();

    return <li className={classes.root}>
        {children}
    </li>
};

const makeNavigationCategoryStyles = makeStyles({
    root: {
        padding: '8px',
        paddingLeft: '20px !important',
        backgroundColor: '#163C66',
        color: '#58C1EE',
        fontSize: '14px !important'
    },
    menuItemWithLowerFontSize: {
        fontSize: '12px !important'
    }
});

const NavigationItemBullet = div({
    paddingTop: '3px',
    paddingLeft: '6px',
    paddingRight: '6px',
    background: '#163C66',
    borderRadius: '12px',
    marginLeft: 'auto',
    marginRight: '5px'
});

const NavigationItem = ({ children, path, isDefault = undefined }) => {
    const current = getIdFromPath(2);

    const styles = makeNavigationItemStyles();
    const classes = (current === path || (!current && isDefault) ? styles.selected + ' ' : '') + styles.root;

    return <li className={classes} onClick={() => navigate(path)}>
        {children}
    </li>
};

const makeNavigationItemStyles = makeStyles({
    root: {
        textTransform: 'none',
        padding: '8px',
        paddingLeft: '20px !important',
        fontSize: '14px !important',
        cursor: 'pointer',
        '&:hover': {
            color: '#58C1EE',
        },
    },
    selected: {
        backgroundColor: '#00345c',
        marginTop: '3px',
        marginBottom: '3px',
    }
});
